/**
 * @file ColumnSharesField.tsx
 * @description Доли колонок из 12 (phase19.16, партия SB120, этап 1;
 * uiux-sb120-stage1 §2.5, вопрос 2; данные db-design-sb120-stage1 §1
 * `columns.shares`):
 *
 * ```
 * Доли (из 12)
 * ┌──────────────────┬┬───────────┐   граница тянется по двенадцатым,
 * │        7         ││     5     │   стрелками — по 1
 * └──────────────────┴┴───────────┘
 *  Колонка 1 [−] 7 [+]   Колонка 2 [−] 5 [+]
 *  Быстро: [▌▌ 6+6] [▌▌ 8+4] [▌▌ 4+8]
 * ```
 *
 * Сумма всегда 12, доля не меньше 1. Пока границу тянут — живой
 * предпросмотр на канве (`columns.shares`), отпустили — запись.
 */

import { Minus, Plus } from "lucide-react";
import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { Label } from "../../../ui/label";
import { OptionCards } from "../../../site-view/OptionCards";
import { useSectionForm } from "../section-form-context";
import { RatioScheme } from "./RatioScheme";
import { bumpShare, canBump, moveBoundary, placeBoundary, quickSchemes, ratioOfShares, sharePercent, sharesBefore } from "../../../../lib/site-builder/column-shares";

interface Props {
  shares: readonly number[];
  onChange: (shares: number[]) => void;
}

const STEP_BUTTON = "inline-flex size-12 items-center justify-center rounded-lg bg-surface-low text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 can-hover:size-8";

export function ColumnSharesField({ shares, onChange }: Props) {
  const { preview } = useSectionForm();
  const bar = useRef<HTMLDivElement | null>(null);
  const [live, setLive] = useState<{ index: number; shares: number[] } | null>(null);
  const shown = live?.shares ?? [...shares];

  function at(event: PointerEvent<HTMLElement>, index: number): number[] {
    const rect = bar.current?.getBoundingClientRect();
    if (!rect || !rect.width || !Number.isFinite(event.clientX)) return shown;
    return placeBoundary(shown, index, ((event.clientX - rect.left) / rect.width) * 12);
  }
  function onPointerDown(event: PointerEvent<HTMLButtonElement>, index: number) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setLive({ index, shares: [...shares] });
  }
  function onPointerMove(event: PointerEvent<HTMLButtonElement>, index: number) {
    if (!live || live.index !== index) return;
    const next = at(event, index);
    if (next.some((v, i) => v !== live.shares[i])) {
      setLive({ index, shares: next });
      preview("columns.shares", next);
    }
  }
  function onPointerUp() {
    if (!live) return;
    const next = live.shares;
    setLive(null);
    if (next.some((v, i) => v !== shares[i])) onChange(next);
    else preview("columns.shares", null);
  }
  /** Перетаскивание прервано (система забрала касание) — откат, без записи. */
  function onPointerCancel() {
    if (!live) return;
    setLive(null);
    preview("columns.shares", null);
  }
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const delta = event.key === "ArrowRight" || event.key === "ArrowUp" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowDown" ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next = moveBoundary(shares, index, delta);
    if (next.some((v, i) => v !== shares[i])) onChange(next);
  }

  const ratio = ratioOfShares(shares);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <Label>Доли (из 12)</Label>
      <div ref={bar} className="flex h-14 min-w-0 select-none overflow-hidden rounded-xl bg-surface-low">
        {shown.map((share, i) => (
          <div key={i} className="contents">
            <span className="flex min-w-0 items-center justify-center bg-primary/15 text-sm font-medium tabular-nums text-on-surface" style={{ flexGrow: share, flexBasis: 0 }}>
              {share}
            </span>
            {i < shown.length - 1 ? (
              <button
                type="button"
                role="slider"
                aria-label={`Граница колонок ${i + 1} и ${i + 2}`}
                aria-valuemin={sharesBefore(shown, i) + 1}
                aria-valuemax={sharesBefore(shown, i + 2) - 1}
                aria-valuenow={sharesBefore(shown, i + 1)}
                aria-valuetext={`колонка ${i + 1} — ${shown[i]} из 12, колонка ${i + 2} — ${shown[i + 1]} из 12`}
                onPointerDown={(e) => onPointerDown(e, i)}
                onPointerMove={(e) => onPointerMove(e, i)}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onKeyDown={(e) => onKeyDown(e, i)}
                // Видимая полоса 12 px, зона касания 48 px — псевдоэлементом поверх соседей.
                className="relative z-10 w-3 shrink-0 cursor-col-resize touch-none bg-primary outline-none before:absolute before:inset-y-0 before:-inset-x-[18px] before:content-[''] focus-visible:ring-2 focus-visible:ring-ring"
              />
            ) : null}
          </div>
        ))}
      </div>
      <div className="flex min-w-0 flex-wrap gap-x-4 gap-y-2">
        {shares.map((share, i) => (
          <div key={i} className="flex items-center gap-1" role="group" aria-label={`Колонка ${i + 1}`}>
            <span className="type-caption w-20 text-on-surface-variant">Колонка {i + 1}</span>
            <button type="button" className={STEP_BUTTON} aria-label={`Колонка ${i + 1}: уже`} disabled={!canBump(shares, i, -1)} onClick={() => onChange(bumpShare(shares, i, -1))}>
              <Minus aria-hidden className="size-4" />
            </button>
            <span className="w-14 text-center text-sm tabular-nums text-on-surface" title={sharePercent(share)}>
              {share}/12
            </span>
            <button type="button" className={STEP_BUTTON} aria-label={`Колонка ${i + 1}: шире`} disabled={!canBump(shares, i, 1)} onClick={() => onChange(bumpShare(shares, i, 1))}>
              <Plus aria-hidden className="size-4" />
            </button>
          </div>
        ))}
      </div>
      <OptionCards
        aria-label="Быстрые схемы долей"
        columns={3}
        value={ratio ?? "custom"}
        onChange={(r) => {
          const scheme = quickSchemes(shares.length).find((s) => s.ratio === r);
          if (scheme) onChange([...scheme.shares]);
        }}
        options={quickSchemes(shares.length).map((s) => ({ value: s.ratio, label: s.shares.join("+"), preview: <RatioScheme shares={s.shares} /> }))}
      />
    </div>
  );
}
